import React, { useEffect, useState, useRef } from "react";

import { COUNTRY_PHONE_CODES_LIST, MILLIS_IN_A_DAY } from "base/constants";
import { DateView, formatDateToString } from "base/ui/date";
import { TagsInput } from "base/ui/misc";
import { Popup } from "base/ui/popups";
import { idToTitle, last, len } from "base/utils";
import { sanitizeToUnderscoreId, useRerender } from "base/utils/common";
import { Trash2, Plus, X, ChevronDown, Search } from "lucide-react";

const DATE_FORMAT_REMAP = {
	"YYYY-MM-DD-HH-mm": "YYYY-MM-DD HH:mm",
}

/* Backend-owned customer tags. Merchants can segment on them, not delete the type. */
const LOCKED_USER_TAGS = new Set([
	"ordered", "checkout", "cancelled",
	"shopify_cart_abandoned", "woocommerce_cart_abandoned",
	"shopify_opt_in", "shopify_opt_out",
	"woocommerce_opt_in", "woocommerce_opt_out",
	"postcheckoutflow", "nudge_capture",
]);

function isLockedUserTag(tag) {
	return LOCKED_USER_TAGS.has(tag)
		|| tag.startsWith("back_in_stock_subscribed_to_");
}

function getRelativeDateLabel(tag, from, to) {

	if (from && !to) {
		return `${idToTitle(tag)} ${from} days ago`;
	}

	return `${idToTitle(tag)} between ${to} and ${from} days`;
}


/** Returns { fmt, is_datetime, input_type } for a date tag_type, or null */
function getDateInputInfo(tag_type) {
	if (!tag_type?.startsWith("date")) return null;
	const fmt_key = tag_type.substring(5); // strip "date-"
	const fmt = DATE_FORMAT_REMAP[fmt_key] || fmt_key;
	const is_datetime = fmt.includes("HH");
	return { fmt, is_datetime, input_type: is_datetime ? "datetime-local" : "date" };
}

function IncludeExcludeDropdown({ negated, onChange }) {
	const [open, setOpen] = useState(false);
	return (
		<div className="tw-relative tw-flex-shrink-0">
			<button
				type="button"
				onClick={() => setOpen((o) => !o)}
				onBlur={() => setTimeout(() => setOpen(false), 150)}
				className="tw-flex tw-items-center tw-gap-0.5 tw-px-2 tw-py-1 tw-text-xs tw-font-semibold tw-rounded-md tw-border"
				style={{ borderColor: "var(--primary-color)" }}
			>
				<span style={{ color: negated ? "#64748b" : "var(--primary-color)" }}>
					{negated ? "Exclude" : "Include"}
				</span>
				<ChevronDown size={10} className="tw-opacity-60" />
			</button>
			{open && (
				<div className="tw-absolute tw-top-full tw-left-0 tw-z-20 tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-shadow-md tw-mt-1 tw-min-w-[80px]">
					<button
						type="button"
						onMouseDown={() => { onChange(false); setOpen(false); }}
						className="tw-block tw-w-full tw-text-left tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold hover:tw-bg-primary-fade"
						style={{ color: "var(--primary-color)" }}
					>
						Include
					</button>
					<button
						type="button"
						onMouseDown={() => { onChange(true); setOpen(false); }}
						className="tw-block tw-w-full tw-text-left tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-slate-500 hover:tw-bg-gray-50"
					>
						Exclude
					</button>
				</div>
			)}
		</div>
	);
}

/** Searchable combobox for picking a tag from tag_types */
function TagCombobox({ tag_types, value, onChange }) {
	const [input_val, setInputVal] = useState(value ? idToTitle(value) : "");
	const [open, setOpen] = useState(false);

	const filtered = Object.entries(tag_types || {}).filter(([tag, tt]) => {
		const label = typeof tt === "object" ? (tt.title || idToTitle(tag)) : idToTitle(tag);
		return (
			!input_val ||
			label.toLowerCase().includes(input_val.toLowerCase()) ||
			tag.toLowerCase().includes(input_val.toLowerCase())
		);
	});

	return (
		<div className="tw-relative tw-w-full" style={{ minWidth: 0 }}>
			<div className="tw-relative">
				<Search size={12} className="tw-absolute tw-left-2 tw-top-1/2 -tw-translate-y-1/2 tw-text-gray-400 tw-pointer-events-none" />
				<input
					type="text"
					className="input-primary"
					style={{ paddingLeft: "1.5rem", paddingTop: "0.375rem", paddingBottom: "0.375rem", fontSize: "0.75rem", lineHeight: "1rem" }}
					placeholder="Search tag"
					value={input_val}
					onChange={(e) => { setInputVal(e.target.value); setOpen(true); }}
					onFocus={() => setOpen(true)}
					onBlur={() => setTimeout(() => setOpen(false), 160)}
				/>
			</div>
			{open && (
				<div className="tw-absolute tw-top-full tw-left-0 tw-z-20 tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-shadow-lg tw-mt-1 tw-max-h-44 tw-overflow-y-auto tw-min-w-full">
					{filtered.length > 0 ? filtered.map(([tag, tt]) => {
						const label = typeof tt === "object" ? (tt.title || idToTitle(tag)) : idToTitle(tag);
						const tag_type = typeof tt === "object" ? tt.type : tt;
						return (
							<div
								key={tag}
								className="tw-px-3 tw-py-2 tw-text-xs tw-cursor-pointer hover:tw-bg-primary-fade tw-text-primary-text"
								onMouseDown={() => {
									onChange(tag, tag_type);
									setInputVal(label);
									setOpen(false);
								}}
							>
								{label}
							</div>
						);
					}) : (
						<div className="tw-px-3 tw-py-2 tw-text-xs tw-text-secondary-text">No tags configured</div>
					)}
				</div>
			)}
		</div>
	);
}

function TagRuleRow({ rule, tag_types, onUpdate, onRemove, conflict_error, layout = "inline" }) { // layout: "inline" | "stacked"
	const date_info = getDateInputInfo(rule.tag_type);
	const is_int = rule.tag_type?.startsWith("int");

	const delete_btn = (
		<button
			type="button"
			onClick={onRemove}
			className="tw-p-1 tw-rounded tw-text-gray-300 hover:tw-text-red-400 hover:tw-bg-red-50 tw-transition-colors tw-flex-shrink-0"
			title="Remove rule"
		>
			<Trash2 size={13} />
		</button>
	);

	if (layout === "stacked") {
		return (
			<div className={`tw-flex tw-flex-col tw-gap-1.5 tw-rounded-lg tw-border tw-px-2 tw-py-1.5 tw-bg-white tw-w-full tw-min-w-0 ${conflict_error ? "tw-border-red-300" : "tw-border-gray-200"}`}>
				<div className="tw-flex tw-items-center tw-gap-1.5">
					<IncludeExcludeDropdown negated={rule.negated} onChange={(negated) => onUpdate({ negated })} />
					<div className="tw-flex-1 tw-min-w-0">
						<TagCombobox tag_types={tag_types} value={rule.tag}
							onChange={(tag, tag_type) => onUpdate({ tag, tag_type, from: null, to: null, equal: null, greater_than: null, less_than: null })}
						/>
					</div>
					{conflict_error && <span className="tw-text-xs tw-text-red-500 tw-flex-shrink-0" title={conflict_error}>⚠</span>}
					{delete_btn}
				</div>
				{rule.tag && date_info && (
					<div className="tw-flex tw-items-center tw-gap-1.5">
						<input type={date_info.input_type}
							className="input-primary tw-flex-1 tw-min-w-0"
							style={{ width: "1px", fontSize: "0.75rem", lineHeight: "1rem", paddingTop: "0.25rem", paddingBottom: "0.25rem" }} aria-label="from"
							onChange={(e) => {
								if (!e.target.value) { onUpdate({ from: null }); return; }
								onUpdate({ from: formatDateToString(new Date(e.target.value), date_info.fmt) });
							}}
						/>
						<span className="tw-text-xs tw-text-secondary-text tw-flex-shrink-0">to</span>
						<input type={date_info.input_type}
							className="input-primary tw-flex-1 tw-min-w-0"
							style={{ width: "1px", fontSize: "0.75rem", lineHeight: "1rem", paddingTop: "0.25rem", paddingBottom: "0.25rem" }} aria-label="to"
							onChange={(e) => {
								if (!e.target.value) { onUpdate({ to: null }); return; }
								onUpdate({ to: formatDateToString(new Date(e.target.value), date_info.fmt) });
							}}
						/>
					</div>
				)}
				{rule.tag && is_int && (
					<input type="number" className="input-primary tw-w-full" placeholder="Value"
						style={{ fontSize: "0.75rem", lineHeight: "1rem", paddingTop: "0.25rem", paddingBottom: "0.25rem" }}
						onChange={(e) => onUpdate({ equal: e.target.value || null })} />
				)}
			</div>
		);
	}

	// inline (default) — fixed widths, single row, untouched
	return (
		<div className={`tw-flex tw-items-center tw-gap-1.5 tw-rounded-lg tw-border tw-px-2 tw-py-1 tw-bg-white ${conflict_error ? "tw-border-red-300" : "tw-border-gray-200"}`}>
			<IncludeExcludeDropdown negated={rule.negated} onChange={(negated) => onUpdate({ negated })} />
			<div style={{ width: "130px", minWidth: 0 }}>
				<TagCombobox tag_types={tag_types} value={rule.tag}
					onChange={(tag, tag_type) => onUpdate({ tag, tag_type, from: null, to: null, equal: null, greater_than: null, less_than: null })}
				/>
			</div>
			{rule.tag && date_info && (
				<div className="tw-flex tw-items-center tw-gap-1.5">
					<input type={date_info.input_type} className="input-primary" style={{ width: "130px", fontSize: "0.75rem", lineHeight: "1rem", paddingTop: "0.25rem", paddingBottom: "0.25rem" }}
						aria-label="from"
						onChange={(e) => {
							if (!e.target.value) { onUpdate({ from: null }); return; }
							onUpdate({ from: formatDateToString(new Date(e.target.value), date_info.fmt) });
						}}
					/>
					<span className="tw-text-xs tw-text-secondary-text tw-flex-shrink-0">to</span>
					<input type={date_info.input_type} className="input-primary" style={{ width: "130px", fontSize: "0.75rem", lineHeight: "1rem", paddingTop: "0.25rem", paddingBottom: "0.25rem" }}
						aria-label="to"
						onChange={(e) => {
							if (!e.target.value) { onUpdate({ to: null }); return; }
							onUpdate({ to: formatDateToString(new Date(e.target.value), date_info.fmt) });
						}}
					/>
				</div>
			)}
			{rule.tag && is_int && (
				<input type="number" className="input-primary" placeholder="Value"
					style={{ width: "5rem", fontSize: "0.75rem", lineHeight: "1rem", paddingTop: "0.25rem", paddingBottom: "0.25rem" }}
					onChange={(e) => onUpdate({ equal: e.target.value || null })} />
			)}
			{conflict_error && <span className="tw-text-xs tw-text-red-500 tw-flex-shrink-0" title={conflict_error}>⚠</span>}
			{delete_btn}
		</div>
	);
}

/** A small chip summarising a completed rule */
function FilterSummaryChip({ rule, onRemove }) {
	const label = idToTitle(rule.tag);
	let suffix = "";
	if (rule.from || rule.to) {
		suffix = `: ${rule.from || "∞"} – ${rule.to || "∞"}`;
	} else if (rule.equal != null && rule.equal !== "") {
		suffix = ` = ${rule.equal}`;
	}
	return (
		<span
			className={`tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-pl-2 tw-pr-1 tw-py-0.5 tw-text-xs tw-font-medium ${
				rule.negated
					? "tw-bg-slate-100 tw-text-slate-600 tw-border tw-border-slate-300"
					: "tw-border tw-border-primary-v2/30 tw-text-primary-v2"
			}`}
			style={!rule.negated ? { backgroundColor: "color-mix(in srgb, var(--primary-color) 10%, white)" } : {}}
		>
			{rule.negated && (
				<span className="tw-font-bold tw-text-slate-500 tw-text-xxs tw-bg-slate-200 tw-rounded tw-px-1 tw-mr-0.5">
					NOT
				</span>
			)}
			{label}{suffix}
			<button
				type="button"
				onClick={onRemove}
				className="tw-opacity-50 hover:tw-opacity-100 tw-ml-0.5 tw-flex tw-items-center"
			>
				<X size={10} />
			</button>
		</span>
	);
}

/** True if date range [from_a, to_a] overlaps [from_b, to_b]. null = unbounded. */
function rangesOverlap(from_a, to_a, from_b, to_b) {
	const a_starts_before_b_ends = !to_b   || !from_a || from_a <= to_b;
	const b_starts_before_a_ends = !to_a   || !from_b || from_b <= to_a;
	return a_starts_before_b_ends && b_starts_before_a_ends;
}

/** Returns a Set of rule IDs that participate in a contradiction. */
function getConflicts(rules) {
	const conflict_ids = new Set();
	const active = rules.filter((r) => r.tag);
	for (let i = 0; i < active.length; i++) {
		for (let j = i + 1; j < active.length; j++) {
			const a = active[i], b = active[j];
			if (a.tag !== b.tag) continue;           // different tags — fine
			if (a.negated === b.negated) continue;   // same mode — fine
			if (rangesOverlap(a.from ?? null, a.to ?? null, b.from ?? null, b.to ?? null)) {
				conflict_ids.add(a.id);
				conflict_ids.add(b.id);
			}
		}
	}
	return conflict_ids;
}

const SEARCH_DEBOUNCE_MS = 300;

/**
 * Tag filter builder panel.
 * Replaces the old TagsInput-based dropdown with a structured rule-builder UI.
 *
 * @param tag_types  { tagKey: "date-YYYY-MM-DD-HH-mm" | "" | { title, type, description } }
 * @param onTagsChange  (tags_with_es_values, operator) => void
 * @param onTextSearch  (text) => void   — optional, shows a text search input
 * @param show_tag_operator  boolean     — show AND/OR when ≥2 rules
 * @param layout  "inline" (default) | "stacked" — stacked puts dates on a second row, use in narrow containers
 */
function SearchTagsWithEsValues({
	tag_types,
	onTagsChange,
	onTextSearch,
	className,
	show_tag_operator = true,
	layout = "inline",
}) {
	const ctx = useRef({ next_id: 0, tags_operator: "AND" }).current;
	const rerender = useRerender();
	const [rules, setRules] = useState([]);
	const [text_search, setTextSearch] = useState("");
	const text_search_timer = useRef(null);
	const on_text_search_ref = useRef(onTextSearch);
	useEffect(() => { on_text_search_ref.current = onTextSearch; });

	const nextId = () => ++ctx.next_id;

	/* Serialise rules → { [key]: "gte:...,lte:..." } matching the existing backend format */
	const buildEsValues = (rules_list) => {
		const result = {};
		for (const rule of rules_list) {
			if (!rule.tag) continue
			const values = [];
			if (rule.from) values.push("gte:" + rule.from);
			if (rule.to) values.push("lte:" + rule.to);
			if (rule.equal != null && rule.equal !== "") {
				values.push(rule.equal === "0" ? 0 : "eq:" + rule.equal);
			}
			if (rule.greater_than) values.push("gte:" + rule.greater_than);
			if (rule.less_than) values.push("lte:" + rule.less_than);
			if (rule.date_greater_than) values.push(`gte:DATE|-${rule.date_greater_than}d`);
			if (rule.date_less_than) values.push(`lte:DATE|+${rule.date_less_than}d`);
			const key = rule.negated ? `not:${rule.tag}` : rule.tag;
			result[key] = values.join(",") || "gte:";
		}
		return result;
	};

	const notify = (new_rules, op) => {
		// Don't fire onTagsChange while any contradiction exists
		if (getConflicts(new_rules).size > 0) return;
		onTagsChange?.(buildEsValues(new_rules), op ?? ctx.tags_operator);
	};

	const addRule = () =>
		setRules((prev) => [...prev, { id: nextId(), tag: "", tag_type: "", negated: false }]);

	const removeRule = (id) => {
		const next = rules.filter((r) => r.id !== id);
		setRules(next);
		notify(next);
	};

	const updateRule = (id, updates) => {
		const next = rules.map((r) => (r.id === id ? { ...r, ...updates } : r));
		setRules(next);
		notify(next);
	};


	const setOperator = (op) => {
		ctx.tags_operator = op;
		notify(rules, op);
		rerender();
	};

	const active_rules = rules.filter((r) => r.tag);
	const current_op = ctx.tags_operator || "AND";
	const conflict_ids = getConflicts(rules);

	return (
		<div className={`tw-flex tw-flex-col tw-gap-2 tw-w-full tw-min-w-0 ${className || ""}`}>
			{/* ── Row 1: Search bar ── */}
			{onTextSearch && (
				<input
					type="text"
					className="input-primary tw-w-full"
					style={{ fontSize: "0.75rem", lineHeight: "1rem", paddingTop: "0.375rem", paddingBottom: "0.375rem" }}
					placeholder="Search by name or phone…"
					value={text_search}
					onChange={(e) => {
						const val = e.target.value;
						setTextSearch(val);
						clearTimeout(text_search_timer.current);
						text_search_timer.current = setTimeout(() => on_text_search_ref.current?.(val), SEARCH_DEBOUNCE_MS);
					}}
				/>
			)}

			{/* ── Row 2: Filter rules + Add Filter + Reset all ── */}
			<div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-w-full tw-min-w-0">
				{rules.map((rule) => (
					<TagRuleRow
						key={rule.id}
						rule={rule}
						tag_types={tag_types}
						layout={layout}
						onUpdate={(updates) => updateRule(rule.id, updates)}
						onRemove={() => removeRule(rule.id)}
						conflict_error={
							conflict_ids.has(rule.id)
								? `Contradicts another "${idToTitle(rule.tag)}" rule — date ranges overlap`
								: null
						}
					/>
				))}

				{Object.keys(tag_types || {}).length > 0 && (
					<button
						type="button"
						onClick={addRule}
						className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-font-semibold tw-text-primary-v2 hover:tw-opacity-70 tw-transition-opacity tw-flex-shrink-0"
					>
						<Plus size={13} />
						Add Filter
					</button>
				)}

				{active_rules.length >= 2 && show_tag_operator && (
					<div className="tw-flex tw-items-center tw-gap-1.5 tw-flex-shrink-0 tw-ml-auto">
						<span className="tw-text-xs tw-text-secondary-text">Match</span>
						<div className="tw-flex tw-rounded-md tw-overflow-hidden tw-border tw-border-gray-200">
							{["AND", "OR"].map((op) => (
								<button
									key={op}
									type="button"
									onClick={() => setOperator(op)}
									className={`tw-px-2.5 tw-py-1 tw-text-xs tw-font-semibold tw-transition-colors ${
										current_op === op ? "tw-text-white" : "tw-bg-white tw-text-gray-500"
									}`}
									style={current_op === op ? { backgroundColor: "var(--primary-color)" } : {}}
								>
									{op}
								</button>
							))}
						</div>
					</div>
				)}
			</div>
		</div>
	);
}

/**
 * 
 * @param {selected} {tag: value}
 * @returns 
 */
function AddTagsWithValues({selected:_selected, tag_types, onTagsChange, search_text_length = 3}){
	const [selected, setSelected] = useState([]);

	useEffect(
		() => {
			setSelected(Object.entries(_selected || {}).map(([tag, value]) => {
				let title = idToTitle(tag);
				if(value){
					title += ": " + value;
				}
				return {"title": title, "tag": tag, "value": value}
			}));
		}, [_selected]
	);
	return (
		<TagsInput
			placeholder={"Add Tags"}
			selected={selected}
			onSelectedChange={
				(selected) => {
					let tags_with_value = Object.fromEntries(
						selected.map((tag) => {return [tag.tag, tag.value || ""]})
					);
					onTagsChange?.(tags_with_value)
				}
			}
			suggestions={
				async (text) => {
					if(text.length < search_text_length) return;
					let suggestions = [];
					for(let [tag, tag_type] of Object.entries(tag_types)){
						if(tag.toLowerCase().startsWith(text.toLowerCase())){
							let ret = {"selection": {"title": idToTitle(tag), "tag": tag}};
							ret.view = (
								<div className='tw-flex tw-gap-1 tw-items-center'>
									<div className='tw-grow'>{idToTitle(tag)}</div>
									{
										tag_type?.startsWith("date")
										?	<input type='datetime-local'
												className="tw-border tw-rounded"
												onChange={(evt) => {
													// to epoch seconds
													let format = tag_type.substring(5); // "date-"
													format = DATE_FORMAT_REMAP[format] || format;
													ret.selection.value = formatDateToString(new Date(evt.target.value), format);
													ret.selection.title = `${idToTitle(tag)} [${ret.selection.value}]`
												}}
												onClick={(evt) => evt.stopPropagation()}
											/>
										:	null
									}
								</div>
							);
							suggestions.push(ret)
						}
					}
					return suggestions;
				}
			}
		/>
	)

}
/* THis will render a component to update tag types */
function TagsWithTypesEditor({tag_types, onUpdated}){
	return (
		<TagsInput
			selected={
				Object.entries(tag_types || {}).map(
					([tag, tag_type]) => {
						let title = idToTitle(tag);
						tag_type && (title += `[${tag_type}]`);
						return {
							"title": title,
							"tag": tag,
							"tag_type": tag_type,
							"can_remove": !isLockedUserTag(tag)
						}
					}
				)
			}
			onSelectedChange={
				(selected) => {
					let selected_tags_with_types = Object.fromEntries(selected.map((tag) => [tag.tag, tag.tag_type || ""]));
					onUpdated?.(selected_tags_with_types);
				}
			}
			suggestions={
				async (text) => {
					if(text.length < 3) return;
					let ret = {"selection": {"title": text, "tag": sanitizeToUnderscoreId(text)}};
					ret.view = (
						<div className='tw-flex tw-flex-align-center'>
							<div className='tw-grow'>{idToTitle(text)}</div>
							<select defaultValue={""}
								onClick={(evt) => evt.stopPropagation()}
								onChange={(evt) => {
									let title = text;
									let tag_type = evt.target.value
									tag_type && (title+= `[${tag_type}]`);
									ret.selection= {
										"title": title,
										"tag": sanitizeToUnderscoreId(text),
										"tag_type": tag_type
									}
								}}
							>
								<option value="">Generic</option>
								<option value="date-YYYY-MM-DD">Date</option>
								<option value="date-YYYY-MM-DD-HH-mm">Date And Time</option>
								<option value="date-MM-DD">Day and Month</option>								
							</select>
						</div>
					);
					return [ret];
				}
			}
		/>
	);
}

function ShowTagsWithValues({tags, tag_types}){
	return (
		<div className="tw-flex tw-gap-1 tw-items-center">
			{
				Object.entries(tags || {}).map(([tag, value]) => {
					return (
						<div key={tag} className='tw-border tw-rounded-xl tw-p-1 tw-px-2'>
							{tag} {value}
						</div>
					)
				})
			}
		</div>
	)
}

function removeCountryCode(phoneNumber) {
	if (!phoneNumber) return '';
	
  // Remove all non-digit characters
  const cleaned = phoneNumber.replace(/\D/g, '');
  
  // Extract dial codes without '+' and sort by length (longest first)
  const dialCodes = COUNTRY_PHONE_CODES_LIST
    .map(country => country.dial_code.replace('+', ''))
    .sort((a, b) => b.length - a.length);
  
  // Find and remove matching country code
  for (const code of dialCodes) {
    if (cleaned.startsWith(code)) {
      return cleaned.slice(code.length);
    }
  }
  
  return cleaned;
}

export {TagsWithTypesEditor, AddTagsWithValues, SearchTagsWithEsValues, ShowTagsWithValues, getRelativeDateLabel, removeCountryCode};
